<script setup>
import { ref, computed } from 'vue';
import CcSavedViews from '@/components/levelling/controlCentre/CcSavedViews.vue';

/**
 * Control-centre filters, registered into the v3 ContextDrawer via
 * useV3Drawer().setDrawerContext() — the chrome already renders the levelling
 * ToC ("On this page"), so the page contributes ONLY its filters here.
 *
 * Faithful to the prototype's LvlControlFilters (lvl/surface.jsx): a curated,
 * levelling-specific facet set — Job family group, Job family, Evaluation
 * status, plus the two optional axes Updated-between (two date pickers) and
 * Evaluator. NO document-type / document-status / region / classifier dropdowns
 * and NO hierarchy "explore" tree — those are out of scope for this page.
 */
// NOTE on prop shapes: these arrive as PLAIN VALUES, not refs. The v3
// ContextDrawer stores props in a `ref({})` (useV3Drawer), whose observer
// unwraps any top-level ref — reactively — into its value. So a `ref([])` in
// the page lands here as an Array, a computed<number> as a Number, etc. Only
// the `markRaw`'d composable objects (data / savedViews) survive as objects
// with their inner refs intact (accessed as `.value`). The non-v3 fallback in
// the page passes the same plain-value shape (a computed) so both paths match.
const props = defineProps({
  data: { type: Object, required: true },          // useLevellingData self object (markRaw)
  savedViews: { type: Object, required: true },    // levellingSavedViews self object (markRaw)
  statusFilter: { type: Array, default: () => [] },        // string[]
  statusOptions: { type: Array, default: () => [] },       // {value,label,count}[]
  docTypeOptions: { type: Array, default: () => [] },      // string[]
  docTypeFilter: { type: Array, default: () => [] },       // string[]
  familyColumns: { type: Array, default: () => [] },       // {field,label}[] (job-family levels)
  evaluatorOptions: { type: Array, default: () => [] },    // string[]
  evaluatorFilter: { type: Array, default: () => [] },     // string[]
  /* Anchor roles (PD-1258). Empty when the feature is off for the tenant, which is
     how the field stays out of "Add more fields" entirely. */
  anchorOptions: { type: Array, default: () => [] },       // {value,label}[]
  anchorFilter: { type: Array, default: () => [] },        // string[]
  updatedFrom: { type: String, default: null },    // 'YYYY-MM-DD'
  updatedTo: { type: String, default: null },      // 'YYYY-MM-DD'
  activeFilterCount: { type: Number, default: 0 },
  // State writes stay with the owning page (vue/no-mutating-props) — the page
  // passes setters alongside the read-only values.
  onClearAll: { type: Function, required: true },
  onSetStatusFilter: { type: Function, required: true },
  onSetDocType: { type: Function, required: true },
  onSetEvaluator: { type: Function, required: true },
  onSetAnchor: { type: Function, default: null },
  onSetUpdated: { type: Function, required: true },
});

// Job family group / family are MULTI-select (like the prototype's LvlMultiField).
// The composable stores them as arrays; normalise a legacy single value too.
function hierArr(field) {
  const v = props.data.hierarchyFieldFilters.value[field];
  return Array.isArray(v) ? v : (v != null && v !== '' ? [v] : []);
}
// Changing a parent hierarchy level (e.g. Job family group) clears any deeper
// child selections in the same hierarchy — group first, then family down. The
// child columns share `position`; deeper levels have a lower `level` number.
// Batches into one filter update + reload.
function setHier(hc, arr) {
  props.data.setFieldFilter('hierarchyFieldFilters', hc.field, Array.isArray(arr) ? arr : []);
  // Clear any deeper (child) levels in the same hierarchy that still hold a
  // selection — group first, then family down.
  (props.familyColumns || []).forEach((c) => {
    if (c.position === hc.position && c.level < hc.level && hierArr(c.field).length) {
      props.data.setFieldFilter('hierarchyFieldFilters', c.field, []);
    }
  });
}

// "Add more fields" — Updated between + Evaluator are optional and hidden until
// added (or when they already carry a value, e.g. restored/applied-from-a-view).
// Removing an added field clears its value.
const OPTIONAL = [
  { key: 'updated', label: 'Updated between' },
  { key: 'evaluator', label: 'Evaluator' },
  // Only offered when the tenant has anchor roles on (the page omits the options).
  { key: 'anchor', label: 'Anchor' },
];
const added = ref(new Set());
const pickerOpen = ref(false);
function hasValue(key) {
  if (key === 'evaluator') return (props.evaluatorFilter || []).length > 0;
  if (key === 'anchor') return (props.anchorFilter || []).length > 0;
  if (key === 'updated') return !!(props.updatedFrom || props.updatedTo);
  return false;
}
function isShown(key) {
  return added.value.has(key) || hasValue(key);
}
function addField(key) {
  const s = new Set(added.value);
  s.add(key);
  added.value = s;
  pickerOpen.value = false;
}
function removeField(key) {
  const s = new Set(added.value);
  s.delete(key);
  added.value = s;
  if (key === 'evaluator') props.onSetEvaluator([]);
  if (key === 'anchor' && props.onSetAnchor) props.onSetAnchor([]);
  else if (key === 'updated') props.onSetUpdated(null, null);
}
/* Don't offer a field that would render nothing: Anchor is only addable when the
   tenant has the feature on (signalled by the page passing options at all). */
const availableToAdd = computed(() =>
  OPTIONAL.filter(o => !isShown(o.key))
    .filter(o => o.key !== 'anchor' || (props.anchorOptions || []).length > 0)
);
</script>

<template>
  <div class="lvl-filt">
    <div class="lvl-filt-head">
      <span class="lvl-filt-title">
        <v-icon size="14">mdi-filter-variant</v-icon>
        Filters
        <span v-if="activeFilterCount > 0" class="lvl-filt-count">{{ activeFilterCount }}</span>
      </span>
      <button v-if="activeFilterCount > 0" class="lvl-filt-clear" @click="onClearAll">Clear all</button>
    </div>

    <!-- Job family group / Job family — the job-architecture hierarchy levels. -->
    <div v-for="hc in familyColumns" :key="'hf' + hc.field" class="lvl-filt-field">
      <label class="lvl-filt-lbl">{{ hc.label }}</label>
      <v-select
        :value="hierArr(hc.field)"
        :items="data.getDistinctValues(hc.field)"
        multiple dense solo flat hide-details clearable
        placeholder="All"
        append-icon="mdi-chevron-down"
        :menu-props="{ closeOnContentClick: false, maxHeight: '400', 'offset-y': true, contentClass: 'cc-overlay' }"
        class="lvl-filt-select"
        @change="setHier(hc, $event)"
      >
        <template v-slot:selection="{ index }">
          <span v-if="index === 0">{{ hierArr(hc.field).length }} selected</span>
        </template>
      </v-select>
    </div>

    <!-- Evaluation status facet (server-side, cross-catalogue). -->
    <div class="lvl-filt-field">
      <label class="lvl-filt-lbl">Evaluation status</label>
      <v-select
        :value="statusFilter"
        :items="statusOptions"
        item-text="label"
        item-value="value"
        multiple dense solo flat hide-details clearable
        placeholder="All statuses"
        append-icon="mdi-chevron-down"
        :menu-props="{ closeOnContentClick: false, maxHeight: '400', 'offset-y': true, contentClass: 'cc-overlay' }"
        class="lvl-filt-select"
        @change="onSetStatusFilter($event || [])"
      >
        <template v-slot:selection="{ index }">
          <span v-if="index === 0">{{ statusFilter.length }} selected</span>
        </template>
      </v-select>
    </div>
    <div v-if="docTypeOptions.length > 1" class="lvl-filt-field">
      <label class="lvl-filt-lbl">Document type</label>
      <v-select
        :value="docTypeFilter"
        :items="docTypeOptions"
        multiple dense solo flat hide-details clearable
        placeholder="All types"
        append-icon="mdi-chevron-down"
        :menu-props="{ closeOnContentClick: false, maxHeight: '400', 'offset-y': true, contentClass: 'cc-overlay' }"
        class="lvl-filt-select"
        @change="onSetDocType($event || [])"
      >
        <template v-slot:selection="{ index }">
          <span v-if="index === 0">{{ docTypeFilter.length }} selected</span>
        </template>
      </v-select>
    </div>

    <!-- Updated between — optional (added via "Add more fields"). -->
    <div v-if="isShown('updated')" class="lvl-filt-field">
      <div class="lvl-filt-lblrow">
        <label class="lvl-filt-lbl">Updated between</label>
        <button class="lvl-filt-remove" title="Remove filter" @click="removeField('updated')"><v-icon size="15">mdi-close</v-icon></button>
      </div>
      <div class="lvl-filt-dates">
        <label class="lvl-filt-drow">
          <span class="lvl-filt-dlbl">From</span>
          <input
            type="date"
            class="lvl-filt-date"
            :value="updatedFrom || ''"
            :max="updatedTo || undefined"
            aria-label="Updated from"
            @change="onSetUpdated($event.target.value || null, updatedTo)"
          />
        </label>
        <label class="lvl-filt-drow">
          <span class="lvl-filt-dlbl">To</span>
          <input
            type="date"
            class="lvl-filt-date"
            :value="updatedTo || ''"
            :min="updatedFrom || undefined"
            aria-label="Updated to"
            @change="onSetUpdated(updatedFrom, $event.target.value || null)"
          />
        </label>
      </div>
    </div>

    <!-- Evaluator — optional (added via "Add more fields"). -->
    <div v-if="isShown('evaluator')" class="lvl-filt-field">
      <div class="lvl-filt-lblrow">
        <label class="lvl-filt-lbl">Evaluator</label>
        <button class="lvl-filt-remove" title="Remove filter" @click="removeField('evaluator')"><v-icon size="15">mdi-close</v-icon></button>
      </div>
      <v-select
        :value="evaluatorFilter"
        :items="evaluatorOptions"
        multiple dense solo flat hide-details clearable
        placeholder="All evaluators"
        append-icon="mdi-chevron-down"
        :menu-props="{ closeOnContentClick: false, maxHeight: '400', 'offset-y': true, contentClass: 'cc-overlay' }"
        class="lvl-filt-select"
        @change="onSetEvaluator($event || [])"
      >
        <template v-slot:selection="{ index }">
          <span v-if="index === 0">{{ evaluatorFilter.length }} selected</span>
        </template>
      </v-select>
    </div>

    <!-- Anchor — optional, and only present when the feature is on. Filters on the
         same overlay data the Anchor column shows, so the column and the filter can
         never disagree. -->
    <div v-if="anchorOptions.length && isShown('anchor')" class="lvl-filt-field">
      <div class="lvl-filt-lblrow">
        <label class="lvl-filt-lbl">Anchor</label>
        <button class="lvl-filt-remove" title="Remove filter" @click="removeField('anchor')"><v-icon size="15">mdi-close</v-icon></button>
      </div>
      <v-select
        :value="anchorFilter"
        :items="anchorOptions"
        item-text="label"
        item-value="value"
        multiple dense solo flat hide-details clearable
        placeholder="Any"
        append-icon="mdi-chevron-down"
        :menu-props="{ closeOnContentClick: false, maxHeight: '400', 'offset-y': true, contentClass: 'cc-overlay' }"
        class="lvl-filt-select"
        @change="onSetAnchor && onSetAnchor($event || [])"
      >
        <template v-slot:selection="{ index }">
          <span v-if="index === 0">{{ anchorFilter.length }} selected</span>
        </template>
      </v-select>
    </div>

    <!-- Add more fields — reveals the optional filters above. -->
    <div v-if="availableToAdd.length" class="lvl-filt-addwrap">
      <button class="lvl-filt-add" @click="pickerOpen = !pickerOpen">
        <v-icon size="15">mdi-plus</v-icon> Add more fields
      </button>
      <template v-if="pickerOpen">
        <div class="lvl-filt-addcatch" @click="pickerOpen = false"></div>
        <div class="lvl-filt-addmenu" role="menu">
          <button v-for="o in availableToAdd" :key="o.key" class="lvl-filt-addopt" @click="addField(o.key)">{{ o.label }}</button>
        </div>
      </template>
    </div>

    <!-- Saved views — quiet section at the foot of the filters (prototype). -->
    <CcSavedViews
      :views="savedViews.views.value"
      :isCurrent="savedViews.isCurrent"
      :canSave="activeFilterCount > 0"
      @apply="savedViews.applyView"
      @deactivate="onClearAll"
      @remove="savedViews.remove"
      @save="savedViews.saveCurrent"
    />
  </div>
</template>

<style lang="scss" scoped>
.lvl-filt {
  padding: 6px 2px 12px;
}

.lvl-filt-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 6px 4px;
  border-top: 1px solid var(--cc-border-soft, #eef0f3);
  margin-top: 6px;
}

/* Prototype lvl-filt-title — 11px/700/upper/.06em/text-3. */
.lvl-filt-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--cc-text-3, #8b94a3);

  .v-icon {
    color: var(--cc-text-3, #8b94a3) !important;
  }
}

/* Prototype lvl-filt-count — accent-tinted, 10.5px/700. */
.lvl-filt-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--cc-nav-accent-soft, rgba(79, 70, 229, 0.14));
  color: var(--cc-nav-accent, #4f46e5);
  font-size: 10.5px;
  font-weight: 700;
}

.lvl-filt-clear {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: var(--cc-nav-accent, #4f46e5);

  &:hover {
    text-decoration: underline;
  }
}

.lvl-filt-field {
  padding: 6px 6px;
}

.lvl-filt-lbl {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--cc-text-2, #47505f);
  margin-bottom: 4px;
}

.lvl-filt-lblrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  .lvl-filt-lbl {
    margin-bottom: 0;
  }
}

.lvl-filt-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--cc-text-3, #8b94a3);
  border-radius: 4px;
  padding: 2px;

  .v-icon {
    color: var(--cc-text-3, #8b94a3) !important;
  }

  &:hover {
    background: var(--cc-surface-2, #f0f2f5);

    .v-icon {
      color: var(--cc-text, #1f242d) !important;
    }
  }
}

.lvl-filt-addwrap {
  position: relative;
  padding: 8px 6px 2px;
}

.lvl-filt-add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px dashed var(--cc-border, #d6dae1);
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: var(--cc-text-2, #47505f);
  border-radius: 7px;
  padding: 6px 10px;

  .v-icon {
    color: inherit !important;
  }

  &:hover {
    border-color: var(--cc-nav-accent, #4f46e5);
    color: var(--cc-nav-accent, #4f46e5);
  }
}

.lvl-filt-addcatch {
  position: fixed;
  inset: 0;
  z-index: 20;
}

.lvl-filt-addmenu {
  position: absolute;
  top: 100%;
  left: 6px;
  z-index: 21;
  min-width: 180px;
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e0e0e0);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(16, 24, 40, 0.16);
  padding: 4px;
}

.lvl-filt-addopt {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: var(--cc-text, #1f242d);
  padding: 7px 10px;
  border-radius: 6px;

  &:hover {
    background: var(--cc-surface-2, #f2f4f7);
  }
}

/* Prototype lvl-filt-select — 500/13px, quiet border. */
::v-deep .lvl-filt-select {
  border: 1px solid var(--cc-border, #e0e0e0);
  border-radius: 7px;

  .v-input__slot {
    background: var(--cc-surface, #ffffff) !important;
    min-height: 36px !important;
    padding: 0 10px !important;
    box-shadow: none !important;
  }

  // Covers Vuetify's own selection chips AND the custom "All" / "N selected"
  // summary spans in the selection slot.
  .v-select__selections {
    font-size: 13px;
    font-weight: 500;
    color: var(--cc-text, #1f242d);
    line-height: 1.3;
  }

  input::placeholder {
    color: var(--cc-text-3, #aaaaaa);
    font-size: 13px;
    font-weight: 400;
  }
}

/* Prototype lvl-filt-dates — the From / To rows for "Updated between". */
.lvl-filt-dates {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lvl-filt-drow {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lvl-filt-dlbl {
  flex: 0 0 34px;
  font-size: 12px;
  font-weight: 600;
  color: var(--cc-text-3, #8b94a3);
}

.lvl-filt-date {
  flex: 1;
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--cc-border, #e0e0e0);
  border-radius: 7px;
  background: var(--cc-surface, #ffffff);
  color: var(--cc-text, #1f242d);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--cc-accent, #1d5bd6);
  }

  // Keep the native calendar glyph legible on the dark theme.
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: var(--cc-datepicker-filter, none);
  }
}
</style>
